import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { sileo } from 'sileo'
import { useApp } from '../context/AppContext'
import ticketService from '../services/ticketService'
import ticketHistoryService from '../services/ticketHistoryService'
import ticketAttachmentService from '../services/ticketAttachmentService'
import { statusService } from '../services/statusService'
import { buildTicketPayload } from '../utils/ticketDraft'
import { employeeName } from '../utils/employee'

/**
 * Envío de un ticket nuevo, común a los tres modos de creación: inserta el
 * ticket, registra la creación en el historial, sube los adjuntos y abre el
 * detalle. La notificación a administradores la crea un trigger de la base.
 *
 * @returns {{
 *   submit: (draft: Object, files?: Array<{file: File}>) => Promise<boolean>,
 *   saving: boolean,
 *   uploading: boolean
 * }}
 */
export default function useCreateTicket() {
  const { user, employee } = useApp()
  const navigate = useNavigate()
  const [initialStatusId, setInitialStatusId] = useState(1)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    let active = true
    statusService.getInitial().then(({ success, data }) => {
      if (active && success && data?.id) setInitialStatusId(data.id)
    })
    return () => {
      active = false
    }
  }, [])

  const submit = useCallback(
    async (draft, files = []) => {
      if (!user?.id) return false
      setSaving(true)

      const payload = buildTicketPayload(draft, {
        userId: user.id,
        statusId: initialStatusId,
      })
      const {
        success,
        data: ticket,
        error,
      } = await ticketService.create(payload)

      if (!success) {
        setSaving(false)
        sileo.error({ title: 'No se pudo crear el ticket', description: error })
        return false
      }

      // El historial no bloquea el flujo si falla.
      ticketHistoryService.log({
        ticketId: ticket.id,
        fieldName: 'Creación',
        oldValue: null,
        newValue: 'Ticket creado',
        changedBy:
          employeeName(employee) || user.user_metadata?.name || 'Usuario',
      })

      if (files.length) {
        setUploading(true)
        const { data } = await ticketAttachmentService.attach(
          ticket.id,
          files,
          user.id
        )
        setUploading(false)
        if (data.failed.length) {
          sileo.warning({
            title: 'Algunos adjuntos no se subieron',
            description: data.failed.join(', '),
          })
        }
      }

      setSaving(false)
      sileo.success({ title: `Ticket #${ticket.id} creado` })
      navigate(`/ticket/${ticket.id}`)
      return true
    },
    [user, employee, initialStatusId, navigate]
  )

  return { submit, saving, uploading }
}
